import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api/alphaClient";
import { Briefcase, Plus, Trash2, Save, CheckCircle2, Layers, Table, Grid } from "lucide-react";

function pctCell(v) {
  if (v === null || v === undefined) return <span className="text-gray-500">—</span>;
  return (
    <span className={`font-semibold ${v >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
      {v >= 0 ? "+" : ""}{v.toFixed(2)}%
    </span>
  );
}

export default function Portfolio({ universe }) {
  const [holdings, setHoldings] = useState([]);
  const [snapshotDate, setSnapshotDate] = useState(null);
  const [newScrip, setNewScrip] = useState("");
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [selGroups, setSelGroups] = useState([]);
  const [selAsOf, setSelAsOf] = useState(null);
  const [selStatus, setSelStatus] = useState("loading");
  const [selErrorMsg, setSelErrorMsg] = useState("");
  const [viewMode, setViewMode] = useState("pivot"); // 'pivot' | 'summary'

  useEffect(() => {
    setStatus("loading");
    setDirty(false);
    api.getPortfolioCurrent(universe)
      .then((data) => {
        setHoldings((data.holdings || []).map((h) => h.scrip));
        setSnapshotDate(data.holdings?.[0]?.snapshot_date ?? null);
        setStatus("ok");
      })
      .catch((err) => {
        setStatus("error");
        setErrorMsg(err.message || "Failed to load portfolio holdings.");
      });
  }, [universe]);

  useEffect(() => {
    let cancelled = false;
    setSelStatus("loading");
    api.getPortfolioSelections(universe)
      .then((data) => {
        if (cancelled) return;
        setSelGroups(data.groups || []);
        setSelAsOf(data.as_of ?? null);
        setSelStatus("ok");
      })
      .catch((err) => {
        if (!cancelled) { setSelStatus("error"); setSelErrorMsg(err.message || "Failed to load selections."); }
      });
    return () => { cancelled = true; };
  }, [universe]);

  function addScrip(e) {
    e.preventDefault();
    const s = newScrip.trim().toUpperCase();
    if (!s || holdings.includes(s)) return;
    setHoldings([...holdings, s]);
    setNewScrip("");
    setDirty(true);
    setSaveSuccess(false);
  }

  function removeScrip(s) {
    setHoldings(holdings.filter((h) => h !== s));
    setDirty(true);
    setSaveSuccess(false);
  }

  async function save() {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await api.setPortfolioHoldings(universe, holdings);
      setDirty(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setErrorMsg(err.message || "Failed to save portfolio.");
    } finally {
      setSaving(false);
    }
  }

  const weight = holdings.length ? (100 / holdings.length).toFixed(1) : "0.0";

  // Pivot Matrix: Scrips (rows) x Groups (columns) with 1D, 5D, 21D momentum
  const pivot = useMemo(() => {
    const returnsByScrip = {};
    const groupsByScrip = {};
    selGroups.forEach((g) => {
      (g.holdings || []).forEach((h) => {
        if (!returnsByScrip[h.scrip]) {
          returnsByScrip[h.scrip] = { pct_1d: h.pct_1d, pct_5d: h.pct_5d, pct_21d: h.pct_21d };
        }
        if (!groupsByScrip[h.scrip]) groupsByScrip[h.scrip] = new Set();
        groupsByScrip[h.scrip].add(g.key);
      });
    });
    const scrips = Object.keys(returnsByScrip).sort();
    return { scrips, returnsByScrip, groupsByScrip };
  }, [selGroups]);

  return (
    <div className="flex flex-col gap-6 h-full overflow-y-auto pr-1">
      {/* Portfolio Holdings Manager Card */}
      <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-5 shadow-lg flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-800/80 pb-3">
          <div>
            <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-blue-400" />
              Live Portfolio Holdings ({universe})
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Snapshot Date: <span className="font-mono text-gray-200">{snapshotDate || "Latest"}</span> · Equal Weight: <span className="font-mono text-blue-400 font-bold">{weight}% per stock</span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            {saveSuccess && (
              <span className="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> Saved!
              </span>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className={`px-4 py-2 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-all shadow-md ${
                dirty && !saving
                  ? "bg-emerald-600 hover:bg-emerald-500 text-white cursor-pointer"
                  : "bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700"
              }`}
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Add Scrip Form */}
        <form onSubmit={addScrip} className="flex gap-2 max-w-md">
          <input
            type="text"
            placeholder="Add scrip symbol (e.g. RELIANCE)..."
            value={newScrip}
            onChange={(e) => setNewScrip(e.target.value)}
            className="flex-1 bg-[#0f1116] border border-gray-800 rounded-lg px-3 py-2 text-xs font-mono text-gray-100 focus:outline-none focus:border-blue-500"
          />
          <button
            type="submit"
            className="px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-lg flex items-center gap-1 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add
          </button>
        </form>

        {/* Scrip Tags Scrollable Grid */}
        <div className="bg-[#0f1116] border border-gray-800 rounded-lg p-3 max-h-[180px] overflow-y-auto">
          {status === "loading" ? (
            <div className="text-xs font-mono text-gray-500 py-4 text-center">Loading holdings...</div>
          ) : holdings.length === 0 ? (
            <div className="text-xs font-mono text-gray-500 py-4 text-center">No active holdings added yet.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {holdings.map((scrip) => (
                <div
                  key={scrip}
                  className="bg-[#14171f] border border-gray-700 text-gray-200 px-3 py-1.5 rounded-md text-xs font-mono font-semibold flex items-center gap-2 shadow-sm"
                >
                  <span>{scrip}</span>
                  <span className="text-blue-400 text-[11px] font-normal font-mono border-l border-gray-700/80 pl-2">
                    {weight}%
                  </span>
                  <button
                    onClick={() => removeScrip(scrip)}
                    className="text-gray-400 hover:text-rose-400 transition-colors ml-1"
                    title="Remove scrip"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Active Strategy Selections Card */}
      <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-5 shadow-lg flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-800/80 pb-3">
          <div>
            <h3 className="text-sm font-bold text-gray-100 uppercase tracking-wider flex items-center gap-2">
              <Layers className="w-4 h-4 text-amber-400" />
              This Week's Strategy Selections ({universe})
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Prices as of: <span className="font-mono text-gray-200">{selAsOf || "Latest Week"}</span> · Trailing returns by trading session
            </p>
          </div>

          {/* View Mode Switcher (Pivot Matrix vs Summary List) */}
          <div className="flex items-center gap-1 bg-[#0f1116] p-1 rounded-lg border border-gray-800 shrink-0">
            <button
              onClick={() => setViewMode("pivot")}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                viewMode === "pivot"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-400 hover:text-gray-200"
              }`}
            >
              <Grid className="w-3.5 h-3.5" /> Momentum Pivot Matrix
            </button>
            <button
              onClick={() => setViewMode("summary")}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                viewMode === "summary"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-400 hover:text-gray-200"
              }`}
            >
              <Table className="w-3.5 h-3.5" /> Strategy Breakdown
            </button>
          </div>
        </div>

        {selStatus === "loading" ? (
          <div className="p-8 text-center text-xs font-mono text-gray-500">Loading strategy selections...</div>
        ) : selStatus === "error" ? (
          <div className="p-6 text-center text-xs font-mono text-rose-400">{selErrorMsg}</div>
        ) : selGroups.length === 0 ? (
          <div className="p-8 text-center text-xs font-mono text-gray-500">No active selections recorded for {universe}.</div>
        ) : viewMode === "pivot" ? (
          /* Scrip x Strategy Momentum Pivot Matrix */
          <div className="overflow-x-auto overflow-y-auto max-h-[500px] border border-gray-800 rounded-lg bg-[#14171f]">
            <table className="text-xs font-mono border-collapse w-full">
              <thead className="sticky top-0 bg-[#1a1d29] text-gray-400 uppercase text-[10px] border-b border-gray-800 z-20">
                <tr>
                  <th rowSpan={2} className="sticky left-0 z-30 bg-[#1a1d29] border-r border-b border-gray-800 text-left font-bold text-gray-300 px-4 py-2.5 align-bottom whitespace-nowrap">
                    Scrip
                  </th>
                  {selGroups.map((g) => (
                    <th
                      key={g.key}
                      colSpan={3}
                      className={`border-l border-b border-gray-800 text-center font-bold text-[11px] py-2 px-2 whitespace-nowrap ${
                        g.type === "live" ? "text-cyan-300 bg-cyan-950/40" : "text-gray-200"
                      }`}
                    >
                      {g.key}
                      {g.type === "live" && <span className="ml-1 text-[9px] uppercase px-1 py-0.5 rounded bg-cyan-950 border border-cyan-800 text-cyan-300">LIVE</span>}
                    </th>
                  ))}
                </tr>
                <tr>
                  {selGroups.flatMap((g) => [
                    <th key={`${g.key}-1d`} className="border-l border-b border-gray-800 text-right font-medium text-gray-500 text-[10px] px-2 py-1">1D</th>,
                    <th key={`${g.key}-5d`} className="border-b border-gray-800 text-right font-medium text-gray-500 text-[10px] px-2 py-1">5D</th>,
                    <th key={`${g.key}-21d`} className="border-b border-gray-800 text-right font-medium text-gray-500 text-[10px] px-2 py-1">21D</th>,
                  ])}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50 text-gray-200">
                {pivot.scrips.map((scrip) => {
                  const r = pivot.returnsByScrip[scrip] || {};
                  return (
                    <tr key={scrip} className="hover:bg-gray-800/40 transition-colors">
                      <td className="sticky left-0 z-10 bg-[#14171f] border-r border-gray-800 px-4 py-2 text-gray-100 font-bold whitespace-nowrap">
                        {scrip}
                      </td>
                      {selGroups.flatMap((g) => {
                        const held = pivot.groupsByScrip[scrip]?.has(g.key);
                        return [
                          <td key={`${scrip}-${g.key}-1d`} className="border-l border-gray-800/40 px-2 py-2 text-right">
                            {held ? pctCell(r.pct_1d) : <span className="text-gray-600">-</span>}
                          </td>,
                          <td key={`${scrip}-${g.key}-5d`} className="px-2 py-2 text-right">
                            {held ? pctCell(r.pct_5d) : <span className="text-gray-600">-</span>}
                          </td>,
                          <td key={`${scrip}-${g.key}-21d`} className="px-2 py-2 text-right">
                            {held ? pctCell(r.pct_21d) : <span className="text-gray-600">-</span>}
                          </td>,
                        ];
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* Strategy Summary Table */
          <div className="overflow-x-auto overflow-y-auto max-h-[450px] border border-gray-800 rounded-lg bg-[#14171f]">
            <table className="w-full text-left text-xs font-mono border-collapse">
              <thead className="sticky top-0 bg-[#1a1d29] text-gray-400 uppercase text-[11px] border-b border-gray-800 z-10">
                <tr>
                  <th className="py-3 px-4">Variant Code</th>
                  <th className="py-3 px-4">Selection Date</th>
                  <th className="py-3 px-4">Scrips Selected</th>
                  <th className="py-3 px-4 text-right">Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50 text-gray-200">
                {selGroups.map((group, idx) => {
                  const scripsList = (group.holdings || []).map((h) => h.scrip);
                  const isLive = group.key === "live_portfolio";
                  return (
                    <tr key={idx} className="hover:bg-gray-800/40 transition-colors">
                      <td className="py-2.5 px-4 font-bold">
                        <span className={isLive ? "text-cyan-300" : "text-blue-400"}>{group.key}</span>
                        {isLive && <span className="ml-1.5 text-[9px] uppercase px-1.5 py-0.5 rounded bg-cyan-950 text-cyan-300 border border-cyan-800">LIVE</span>}
                      </td>
                      <td className="py-2.5 px-4 text-gray-400">{group.selection_date}</td>
                      <td className="py-2.5 px-4 max-w-lg">
                        <div className="flex flex-wrap gap-1">
                          {scripsList.map((s) => (
                            <span key={s} className="bg-[#0f1116] border border-gray-800 px-1.5 py-0.5 rounded text-[11px] text-gray-300 font-bold">
                              {s}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-right font-bold text-gray-100">{scripsList.length}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
